import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Menu, MessageCircle, X } from "lucide-react";
import { INSTAGRAM_URL, NAV_ITEMS, WHATSAPP_URL, scrollToHash } from "@/lib/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(hash);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className="sticky top-0 z-50 border-b border-[#D4AF37]/20 bg-[#FDFBF7]/90 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 md:h-20 max-w-7xl items-center justify-between px-6 md:px-10">
          <a
            href="#hero"
            onClick={(e) => go(e, "#hero")}
            data-testid="nav-logo"
            className="font-serif text-lg md:text-xl tracking-[0.18em] text-[#1A1818]"
          >
            DONNA <span className="text-[#A98A2C]">VIRTUOSA</span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigazione principale">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.hash}
                href={item.hash}
                onClick={(e) => go(e, item.hash)}
                data-testid={item.testid}
                className="text-xs font-sans font-medium uppercase tracking-[0.18em] text-[#3A3532] transition-colors duration-300 hover:text-[#A98A2C]"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-cta"
              className="hidden items-center gap-2 rounded-sm bg-[#1A1818] px-5 py-2.5 text-xs font-sans font-semibold uppercase tracking-[0.15em] text-[#FDFBF7] transition-colors duration-300 hover:bg-[#A98A2C] md:inline-flex"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
              Parla con Chiara
            </a>
            <button
              type="button"
              aria-label="Apri menu"
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center text-[#1A1818] lg:hidden"
            >
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#FDFBF7]"
            data-testid="nav-mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-6">
              <span className="font-serif text-lg tracking-[0.18em] text-[#1A1818]">
                DONNA <span className="text-[#A98A2C]">VIRTUOSA</span>
              </span>
              <button
                type="button"
                aria-label="Chiudi menu"
                data-testid="nav-menu-close"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center text-[#1A1818]"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Menu mobile">
              {NAV_ITEMS.map((item, i) => (
                <motion.a
                  key={item.hash}
                  href={item.hash}
                  onClick={(e) => go(e, item.hash)}
                  data-testid={`${item.testid}-mobile`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  className="border-b border-[#2A2421]/10 py-5 font-serif text-3xl text-[#1A1818]"
                >
                  {item.label}
                </motion.a>
              ))}
              <motion.a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-whatsapp-cta-mobile"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-10 inline-flex items-center justify-center gap-3 rounded-sm bg-[#1A1818] px-8 py-4 text-sm font-sans font-semibold uppercase tracking-[0.15em] text-[#FDFBF7]"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                Parla con Chiara
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const MobileBottomCTA = () => (
  <div
    data-testid="mobile-sticky-cta"
    className="fixed inset-x-0 bottom-0 z-50 border-t border-[#D4AF37]/25 bg-[#FDFBF7]/95 p-3 backdrop-blur-xl lg:hidden"
  >
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="mobile-sticky-cta-button"
      className="flex items-center justify-center gap-2 rounded-sm bg-[#1A1818] py-3.5 text-sm font-sans font-semibold uppercase tracking-[0.15em] text-[#FDFBF7]"
    >
      <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
      Parla con Chiara
    </a>
  </div>
);

export const Footer = () => (
  <footer data-testid="footer" className="bg-[#1A1818] text-[#FDFBF7]">
    <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
      <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="font-serif text-2xl tracking-[0.15em]">
            DONNA <span className="text-[#D4AF37]">VIRTUOSA</span>
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#FDFBF7]/60">
            Il percorso di Life Coaching Cristiano 1:1 di Chiara Perrone, per donne che vogliono
            mettere Dio al centro della propria vita.
          </p>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37] transition-colors duration-300 hover:text-[#FDFBF7]"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.75} />
            Instagram
          </a>
        </div>
        <nav className="flex flex-col gap-3" aria-label="Link legali">
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-[#FDFBF7]/70 transition-colors duration-300 hover:text-[#D4AF37]">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" data-testid="footer-cookie-link" className="text-sm text-[#FDFBF7]/70 transition-colors duration-300 hover:text-[#D4AF37]">
            Cookie Policy
          </Link>
          <Link to="/termini" data-testid="footer-termini-link" className="text-sm text-[#FDFBF7]/70 transition-colors duration-300 hover:text-[#D4AF37]">
            Termini e Condizioni
          </Link>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-contatti-link" className="text-sm text-[#FDFBF7]/70 transition-colors duration-300 hover:text-[#D4AF37]">
            Contatti
          </a>
          <span className="text-sm text-[#FDFBF7]/70" data-testid="footer-ffe">Fede &amp; Fitness Italia</span>
        </nav>
      </div>
      <div className="mt-14 border-t border-[#FDFBF7]/10 pt-8">
        <p className="max-w-4xl text-xs leading-relaxed text-[#FDFBF7]/60" data-testid="footer-disclaimer">
          Donna Virtuosa è un servizio di Life Coaching e crescita personale con orientamento
          cristiano. Non costituisce psicoterapia, assistenza psicologica, psichiatrica, medica,
          nutrizionale o sanitaria e non sostituisce il supporto di professionisti qualificati
          quando necessario. Il coaching non garantisce risultati specifici. Gli eventuali risultati
          dipendono dalla situazione individuale, dall&rsquo;impegno personale e da molteplici altri
          fattori.
        </p>
        <p className="mt-6 text-xs text-[#FDFBF7]/35">
          © {new Date().getFullYear()} Donna Virtuosa — Chiara Perrone. Tutti i diritti riservati.
        </p>
      </div>
    </div>
  </footer>
);
